import React, { useState } from "react";
import PageHeader from "./PageHeader.jsx";
import WelcomeModal from "./WelcomeModal.jsx";

export default function SignupPage({ onLogin, onBackToLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [error, setError] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const checkUsername = async (username) => {
    if (!username.trim()) {
      setUsernameAvailable(null);
      return;
    }
    
    setIsCheckingUsername(true);
    try {
      const res = await fetch(`/api/check-username/${encodeURIComponent(username)}`);
      const data = await res.json();
      setUsernameAvailable(data.available);
    } catch (err) {
      console.error('Error checking username:', err);
    }
    setIsCheckingUsername(false);
  };

  const handleUsernameChange = (e) => {
    const newUsername = e.target.value;
    setUsername(newUsername);
    setError('');
    
    // Debounce username checking
    clearTimeout(window.usernameCheckTimeout);
    window.usernameCheckTimeout = setTimeout(() => {
      checkUsername(newUsername);
    }, 500);
  };

  const handleSignup = async () => {
    setError('');

    // Validation
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    
    if (username.length < 3) {
      setError('Username must be at least 3 characters long');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (usernameAvailable === false) {
      setError('Username is already taken');
      return;
    }

    try {
      const res = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username.trim(), password }),
        credentials: 'include',
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        setError(errorData.error || 'Signup failed');
        return;
      }
      
      const data = await res.json();
      setShowWelcomeModal(true);
      window.tempUserData = data;
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    if (window.tempUserData) {
      onLogin(window.tempUserData);
      delete window.tempUserData;
    }
  };

  const getUsernameStatus = () => {
    if (isCheckingUsername) return { text: 'Checking...', color: '#718096' };
    if (usernameAvailable === true) return { text: '✓ Available', color: '#48bb78' };
    if (usernameAvailable === false) return { text: '✗ Taken', color: '#f56565' };
    return null;
  };

  const usernameStatus = getUsernameStatus();
  const canSubmit = username.trim() && password && confirmPassword && 
                   password === confirmPassword && usernameAvailable === true;

  return React.createElement('div', { className: 'page login-page' },
    React.createElement('div', { className: 'login-box' },
      React.createElement(PageHeader, {
        title: 'Create Your Account',
        description: 'Join Kinder and start finding your perfect name!'
      }),
      
      React.createElement('div', { className: 'signup-form' },
        React.createElement('div', { className: 'input-group' },
          React.createElement('input', {
            placeholder: 'Choose a username',
            value: username,
            onChange: handleUsernameChange,
            className: usernameAvailable === false ? 'input-error' : ''
          }),
          usernameStatus && React.createElement('div', { 
            className: 'input-status',
            style: { color: usernameStatus.color }
          }, usernameStatus.text)
        ),
        
        React.createElement('input', {
          type: 'password',
          placeholder: 'Create a password (min 6 characters)',
          value: password,
          onChange: e => { setPassword(e.target.value); setError(''); }
        }),
        
        React.createElement('div', { className: 'input-group' },
          React.createElement('input', {
            type: 'password',
            placeholder: 'Confirm your password',
            value: confirmPassword,
            onChange: e => { setConfirmPassword(e.target.value); setError(''); },
            className: confirmPassword && password !== confirmPassword ? 'input-error' : ''
          }),
          confirmPassword && password !== confirmPassword && 
            React.createElement('div', { 
              className: 'input-status',
              style: { color: '#f56565' }
            }, '✗ Passwords do not match')
        ),
        
        error && React.createElement('div', { className: 'error-message' }, error),
        
        React.createElement('div', { className: 'signup-buttons' },
          React.createElement('button', {
            onClick: handleSignup,
            disabled: !canSubmit,
            className: canSubmit ? 'btn-primary' : 'btn-secondary'
          }, 'Create Account'),
          React.createElement('button', {
            onClick: onBackToLogin,
            className: 'btn-outline'
          }, 'Back to Login')
        ),
        React.createElement('p', {
          className: 'auth-disclaimer'
        },
          'This auth was created with ChatGPT and should NOT be trusted with serious information or passwords.'
        )
      )
    ),
    React.createElement(WelcomeModal, {
      isOpen: showWelcomeModal,
      onClose: handleWelcomeModalClose
    })
  );
}
